import { useMemo, useState } from 'react'
import { BlockComponentFactory } from 'shared/express-interface/blocks-core/BlockFactory'
import Flex from 'antd/lib/flex'
import Card from 'antd/lib/card'
import Title from 'antd/lib/typography/Title'
import { ArrowsAltOutlined } from '@ant-design/icons'
import HtmlRenderer from 'Utils/htmlRenederer.utils'
import styles from './ResponsesBlock.module.less'
import { ResponseModal } from 'Components/modal/ResponseModal/ResponseModal'
import { ConversationNode } from 'Types/Tasks.types'
import { Signal } from '@preact/signals-react'
import { useTaskContext } from 'Contexts/TaskContext'

export type ResponsesBlockProps = {
    layout?: Signal<'single-row-view' | 'grid-view'>
    updateSelectedResponseId: (id: string) => void
    updateHoveredResponseId: (id: string) => void
    responses: ConversationNode[]
}

export const ResponsesBlockComponent =
    BlockComponentFactory.createBlockComponent<ResponsesBlockProps>(props => {
        const taskContext = useTaskContext()
        const [expandedResponseId, setExpandedResponseId] = useState('')
        const layout = props.layout?.value ?? 'single-row-view'

        const cards = useMemo(() => {
            return props.responses?.map((response, index) => (
                <Card
                    key={response.id}
                    className={`${styles.responseContainer} ${
                        layout === 'single-row-view' ? styles.singleRow : styles.multiRow
                    }`}
                    classNames={{
                        body: styles.cardBody,
                        header: styles.cardHeader,
                        title: styles.cardTitle,
                    }}
                    title={
                        <>
                            <Title level={5} className={styles.title}>
                                Response <span>{index + 1}</span>
                            </Title>
                            <ArrowsAltOutlined
                                className={styles.expandIcon}
                                onClick={() => setExpandedResponseId(response.id)}
                            />
                        </>
                    }
                    onClick={() => props.updateSelectedResponseId(response.id)}
                    onMouseEnter={() => props.updateHoveredResponseId(response.id)}
                    onMouseLeave={() => props.updateHoveredResponseId('')}
                >
                    <HtmlRenderer
                        htmlContent={response.content.data.value as string}
                        className={styles.htmlRenderer}
                    />
                </Card>
            ))
        }, [layout, props])

        const responseComparison = useMemo(() => {
            if (!cards?.length) return null
            let component = null
            if (layout === 'single-row-view') {
                component = <div className={styles.responseLayout}>{cards}</div>
            } else {
                component = (
                    <Flex wrap gap={10} className={styles.multiRowResponseLayout}>
                        {cards}
                    </Flex>
                )
            }

            return (
                <Flex vertical gap={10}>
                    {component}
                </Flex>
            )
        }, [cards, layout])

        return (
            <>
                {responseComparison}
                <ResponseModal
                    visible={expandedResponseId.length > 0}
                    responseIndex={
                        taskContext
                            .getInputDetails()
                            .data.data.responses?.findIndex(
                                ratingsAttribute => ratingsAttribute.id === expandedResponseId
                            ) ?? -1
                    }
                    content={
                        (taskContext
                            .getInputDetails()
                            .data.data.responses?.find(
                                ratingsAttribute => ratingsAttribute.id === expandedResponseId
                            )?.content.data.value as unknown as string) ?? null
                    }
                    onClose={() => setExpandedResponseId('')}
                />
            </>
        )
    })
